import React, { useState, useRef, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { getPageTransitionProps } from "../../utils/pageTransition";
import { Link, useNavigate } from "react-router-dom";
import { useAndroidSafeArea } from "../../hooks/useAndroidSafeArea";
import { useActiveDomain, DOMAIN_PR } from "../../utils/domain";
import { PromoTimer, readStoredStatus } from "./Page";
import { FiSettings, FiKey, FiAlertCircle, FiFlag, FiZap, FiInfo } from "react-icons/fi";

import NavTestPage from "./NavTestPage";
import NotebookBackground from "../../img/NotebookBackground";
import deltaLogo from '../../img/logo_full.png';
import ContactModal from "./ContactModal";
import AboutMatematlyModal from "./AboutMatematlyModal";

const PageTestPage = ({ showNav, children, accountStatus, dailyGoalProgress, reportIssueData, solidBg }) => {
  const isLoggedIn = !!localStorage.getItem('token');

  const [isChatMenuOpen, setIsChatMenuOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [contactModalType, setContactModalType] = useState(null);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  
  const [localAccountStatus, setLocalAccountStatus] = useState(readStoredStatus);

  const chatMenuRef = useRef(null);
  const navigate = useNavigate();

  const prefersReducedMotion = useReducedMotion();
  const transitionProps = getPageTransitionProps(prefersReducedMotion);

  const { paddingTop, paddingBottom } = useAndroidSafeArea();

  const headerHeight = `calc(3rem + ${paddingTop})`;
  const footerPadding = paddingBottom;
  const mainContentPaddingBottom = showNav
      ? `calc(4rem + ${paddingBottom})`
      : paddingBottom;

  useEffect(() => {
    if (!accountStatus) setLocalAccountStatus(readStoredStatus());
  }, [accountStatus]);

  const activeStatus = accountStatus || localAccountStatus;
  const activeDomain = useActiveDomain();

  // SPRAWDZAMY CZY JEST AKTYWNA OFERTA (Dla logiki ukrywania paska)
  const hasActiveTimer = activeStatus?.promo_offer_expires_at && activeStatus.promo_offer_expires_at > Math.floor(Date.now() / 1000);

  // Ta sama reguła nazw co w Page.js: w trialu "Przedłuż dostęp" nie mówi, że to cennik.
  const accessLeft = (activeStatus?.access_expires_at_timestamp || 0) - Date.now() / 1000;
  const isInTrial = accessLeft > 0 && accessLeft <= 72 * 3600;
  const upgradeLabel = hasActiveTimer ? "Cennik · dziś taniej" : isInTrial ? "Cennik" : "Przedłuż dostęp";

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (chatMenuRef.current && !chatMenuRef.current.contains(event.target)) {
        setIsChatMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const openContactModal = (type) => {
    setContactModalType(type);
    setIsContactModalOpen(true);
    setIsChatMenuOpen(false);
  };
  
  const handleAboutClick = () => {
    setIsAboutModalOpen(true);
    setIsChatMenuOpen(false);
  };

  const handleUpgradeClick = () => {
    navigate('/upgrade');
    setIsChatMenuOpen(false);
  };

  return (
    <div className={`relative min-h-[100dvh] w-full flex flex-col${solidBg ? ' bg-white' : ''}`}>

      {!solidBg && <NotebookBackground />}

      <header className="w-full bg-transparent z-10 lg:sticky lg:top-0 lg:bg-white lg:shadow-[0_4px_15px_rgba(0,0,0,0.40)]">
        <div className="max-w-[90rem] mx-auto px-4 lg:px-6">
          <div 
            className="relative flex justify-between items-center"
            style={{
                paddingTop: paddingTop,
                height: headerHeight
            }}
          >
            {/* LEWA STRONA */}
            <div className="flex items-center gap-4">
              <Link to="/end-session-stats" className="flex items-center gap-2 flex-shrink-0">
                <img src={deltaLogo} alt="Matemaly logo" className="h-9 w-9" />
                <span className="font-apotek text-xl text-[#233855] tracking-wider">MATEMATLY</span>
                {activeDomain === DOMAIN_PR && (
                  <span className="font-apotek text-xl text-[#E09F2E] tracking-wider">(PR)</span>
                )}
              </Link>
              
              {/* --- LOGIKA WARUNKOWA --- */}
              {/* Jeśli jest timer -> hidden na mobile (md:flex). */}
              {/* Jeśli nie ma timera -> flex (zawsze widoczny). */}
              {showNav && activeStatus && (
                <div className={`w-32 items-center ${hasActiveTimer ? 'hidden md:flex' : 'flex'}`}> 
                  <div className="relative w-full">
                    <div className="w-full bg-gray-200 rounded-full h-2 shadow-inner">
                      <div
                        className="bg-gradient-to-r from-yellow-200 to-[#F8AB3B] h-2 rounded-full transition-all duration-500"
                        style={{ width: `${dailyGoalProgress || 0}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ŚRODEK */}
            {showNav && (
  <div className="hidden xl:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md">
    <NavTestPage />
  </div>
)}

            {/* PRAWA STRONA */}
            <div className="flex items-center">
              
              {/* LICZNIK */}
              {activeStatus?.promo_offer_expires_at && (
                  <PromoTimer 
                      expiresAt={activeStatus.promo_offer_expires_at} 
                      onClick={() => navigate('/upgrade')}
                  />
              )}

              {/* USTAWIENIA MENU */}
              <div className="relative" ref={chatMenuRef}>
                <button
                  onClick={() => setIsChatMenuOpen(prev => !prev)}
                  aria-label="Ustawienia i menu"
                  className="transition-opacity hover:opacity-80 flex-shrink-0 flex items-center justify-center p-1"
                >
                  <FiSettings className="h-6 w-6 text-[#233855]" />
                </button>

                {isChatMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] z-20 border border-gray-100 overflow-hidden">
                    <div className="py-1.5">

                      {isLoggedIn && (
                        <>
                          <button onClick={() => { navigate('/settings'); setIsChatMenuOpen(false); }} className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-[#233855] hover:bg-gray-50 transition-colors">
                            <FiSettings size={15} className="text-[#233855] flex-shrink-0" />
                            Ustawienia
                          </button>
                          <button onClick={handleUpgradeClick} className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-[#233855] hover:bg-gray-50 transition-colors">
                            <FiKey size={15} className="text-[#E09F2E] flex-shrink-0" />
                            {upgradeLabel}
                          </button>
                          <hr className="my-1 border-t border-gray-100" />
                        </>
                      )}

                      <button onClick={() => openContactModal('page-issue')} className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                        <FiAlertCircle size={15} className="text-gray-400 flex-shrink-0" />
                        Zgłoś problem ze stroną
                      </button>

                      <button onClick={() => openContactModal('task-issue')} className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                        <FiFlag size={15} className="text-gray-400 flex-shrink-0" />
                        Zgłoś problem z zadaniem
                      </button>

                      <button onClick={() => openContactModal('suggestion')} className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                        <FiZap size={15} className="text-gray-400 flex-shrink-0" />
                        Zaproponuj ulepszenie
                      </button>

                      <hr className="my-1 border-t border-gray-100" />

                      <button onClick={handleAboutClick} className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                        <FiInfo size={15} className="text-gray-400 flex-shrink-0" />
                        Więcej o Matematly
                      </button>

                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <motion.main
        {...transitionProps}
        className="relative z-0 flex flex-col flex-1 overflow-y-auto overflow-x-hidden"
        style={{ paddingBottom: mainContentPaddingBottom }}
      >
        {children}
      </motion.main>
      
      {showNav && (
        <div
            data-bottom-nav
            className="block xl:hidden fixed bottom-0 left-0 right-0 bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.40)] z-50"
            style={{
                paddingBottom: footerPadding
            }}
        >
            <NavTestPage />
        </div>
      )}

      {isContactModalOpen && (
        <ContactModal
          onClose={() => setIsContactModalOpen(false)}
          type={contactModalType}
          issueData={reportIssueData} 
        />
      )}
      
      {isAboutModalOpen && (
        <AboutMatematlyModal
          onClose={() => setIsAboutModalOpen(false)}
        />
      )}
    </div>
  );
};

export default PageTestPage;